import { TierPill } from "./EngagementBadges";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import MiniSparkline from "../../Container/DiscoveryHub/MiniSparkline";
import { fmtScore, MCI_MUTED } from "../../Utils/Helper/sponsorEngagementUi";

type PerformancePoll = {
  cycle_id: string;
  poll_name?: string;
  index?: number;
  period?: string;
  published?: boolean;
  published_at?: string | null;
};

type PerformanceAccount = {
  account_id: string;
  account_name: string;
  theater?: string;
  region?: string;
  sponsor_role?: "primary" | "secondary" | string | null;
  poll_values?: Record<string, number | null>;
  composite?: number | null;
  tier?: string | null;
  trend_5q?: (number | null)[];
};

const sponsorRoleLabel = (role?: string | null): string | null => {
  if (role === "primary") return "Primary Sponsor";
  if (role === "secondary") return "Secondary Sponsor";
  return null;
};

type Props = {
  polls: PerformancePoll[];
  accounts: PerformanceAccount[];
  displayPeriod: string;
};

// A quarter always shows two poll columns even if only one poll exists yet.
const MIN_POLL_COLUMNS = 2;

const PerformanceAccountBreakdown = ({
  polls,
  accounts,
  displayPeriod,
}: Props) => {
  const displayPolls: PerformancePoll[] = [...polls];
  for (let i = displayPolls.length; i < MIN_POLL_COLUMNS; i += 1) {
    displayPolls.push({ cycle_id: `__placeholder_${i}` });
  }

  const pollColWidth = (22 / displayPolls.length).toFixed(2);
  const totalAccounts = accounts.length;

  if (totalAccounts === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm h-full">
        <div className="py-3.5 px-4 flex items-center justify-between gap-2 text-[13.5px] font-bold text-gray-800 border-b border-gray-200">
          <span>Account Breakdown</span>
          <span className={`text-[11px] font-normal ${MCI_MUTED}`}>
            0 accounts · {displayPeriod}
          </span>
        </div>
        <p className={`m-0 py-7 px-4 text-center text-[13px] ${MCI_MUTED}`}>
          No account performance data for this period.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden h-full">
      <div className="py-3.5 px-4 flex items-center justify-between gap-2 text-[13.5px] font-bold text-gray-800 border-b border-gray-200">
        <span>Account Breakdown</span>
        <span className={`text-[11px] font-normal ${MCI_MUTED}`}>
          {totalAccounts} {totalAccounts === 1 ? "account" : "accounts"} ·{" "}
          {displayPeriod}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] table-fixed border-collapse text-[13px]">
          <colgroup>
            <col className="w-[34%]" />
            {displayPolls.map(poll => (
              <col key={poll.cycle_id} style={{ width: `${pollColWidth}%` }} />
            ))}
            <col className="w-[12%]" />
            <col className="w-[14%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead>
            <tr>
              <th className="bg-gray-50 py-2.5 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200">
                Account
              </th>
              {displayPolls.map((poll, i) => {
                const label = `Poll ${i + 1}`;
                const fullName = poll.poll_name?.trim();
                return (
                  <th
                    key={poll.cycle_id}
                    className="bg-gray-50 py-2.5 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200"
                  >
                    {fullName ? (
                      <TruncatedTooltip tooltip={fullName}>
                        {label}
                      </TruncatedTooltip>
                    ) : (
                      <span className="block">{label}</span>
                    )}
                  </th>
                );
              })}
              <th className="bg-gray-50 py-2.5 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200">
                Composite
              </th>
              <th className="bg-gray-50 py-2.5 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200">
                Tier
              </th>
              <th className="bg-gray-50 py-2.5 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200">
                Trend(5Q)
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(acct => (
              <tr
                key={acct.account_id}
                className="hover:bg-gray-50 last:[&>td]:border-b-0"
              >
                <td className="py-2.5 px-4 border-b border-gray-200 align-middle text-left">
                  <div className="font-semibold text-gray-800">
                    {acct.account_name}
                  </div>
                  <div className={`text-[11px] ${MCI_MUTED}`}>
                    {[
                      ...(acct.theater === acct.region
                        ? [acct.theater]
                        : [acct.theater, acct.region]),
                      sponsorRoleLabel(acct.sponsor_role),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                {displayPolls.map(poll => (
                  <td
                    key={poll.cycle_id}
                    className="py-2.5 px-4 border-b border-gray-200 align-middle text-left tabular-nums"
                  >
                    {fmtScore(acct.poll_values?.[poll.cycle_id], 1)}
                  </td>
                ))}
                <td className="py-2.5 px-4 border-b border-gray-200 align-middle text-left tabular-nums">
                  <strong>{fmtScore(acct.composite, 2)}</strong>
                </td>
                <td className="py-2.5 px-4 border-b border-gray-200 align-middle text-left">
                  {acct.tier ? <TierPill tier={acct.tier} /> : "—"}
                </td>
                <td className="py-2.5 px-4 border-b border-gray-200 align-middle text-left">
                  <MiniSparkline series={acct.trend_5q ?? []} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceAccountBreakdown;
